'use client'

import { trackPricingCompareTableSwitched } from '@/lib/analytics'
import { useState } from 'react'
import { Check } from 'lucide-react'
import { BYOK_PLANS } from './byokPlanData'
import { byokPlanPrice } from './launchOffer'
import { useLaunchOffer } from './useLaunchOffer'

type Cell = string | boolean
type Table = { title: string; columns: { name: string; price: string }[]; rows: [string, ...Cell[]][] }

// Both plan families as one kind of table: plan names across, a quiet label column, hairlines only.
// ponytail: rows restate PlanCards / ByokPlans by hand; keep them in step when a plan changes.
const OLIVE_PLANS: Table = {
  title: 'Standard plans',
  columns: [
    { name: 'Indie', price: 'Free' },
    { name: 'Pro', price: '$30/mo' },
    { name: 'Studio', price: '$150/mo' },
    { name: 'Enterprise', price: 'Custom' },
  ],
  rows: [
    ['Olives a month', false, '300', '1,500', 'Custom'],
    ['MCP and CLI', true, true, true, true],
    ['Generation API', 'With your fal key', 'On request', 'On request', true],
    ['Every model', true, true, true, true],
    ['Commercial license', true, true, true, true],
    ['Olives roll over 12 months', true, true, true, true],
    ['Private projects', false, true, true, true],
    ['Early access to new features', false, true, true, true],
    ['Shared team olives and permissions', false, false, true, true],
    ['Olive spend per project', false, false, true, true],
    ['Billing access for finance', false, false, false, true],
    ['Support SLAs', false, false, false, true],
    ['Hands-on production support', false, false, false, true],
  ],
}

// The header shows what a new member pays a month: the launch price, unless ?offer=off (launchOffer.ts).
const byokTable = (offer: boolean): Table => ({
  title: 'With your own fal key',
  columns: BYOK_PLANS.map(p => ({ name: p.name, price: `${byokPlanPrice(p, { offer }).price}/mo` })),
  rows: [
    ['Generation billed to', 'Your fal account', 'Your fal account', 'Your fal account'],
    ['Every model', true, true, true],
    ['Olives a month, for models not on fal', false, '50', '250'],
    ['Processing minutes a month', ...BYOK_PLANS.map(p => p.processingMinutes)],
    ['MCP, CLI and API', true, true, true],
    ['Olive top-ups', true, true, true],
    ['Share links and XML export', true, true, true],
    ['Private projects', false, true, true],
    ['Early access to new features', false, true, true],
    ['Team workspace', false, false, true],
    ['Team and permissions', false, false, true],
    ['Estimated fal spend per project', false, false, true],
  ],
})

function Value({ value }: { value: Cell }) {
  if (value === true) return <Check className="mx-auto h-4 w-4 text-white/80" strokeWidth={2} aria-label="Included" />
  if (value === false)
    return (
      <span className="text-white/20" aria-label="Not included">
        —
      </span>
    )
  return <span className="text-white/80">{value}</span>
}

function PlanTable({ table }: { table: Table }) {
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] table-fixed text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="w-[34%] py-4" />
              {table.columns.map(c => (
                <th key={c.name} className="py-4 text-center font-medium text-white">
                  {c.name}
                  <span className="mt-0.5 block text-xs font-normal text-white/40">{c.price}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map(([label, ...cells]) => (
              <tr key={label} className="border-b border-white/[0.06]">
                <td className="py-3.5 text-white/55">{label}</td>
                {cells.map((c, i) => (
                  <td key={i} className="py-3.5 text-center tabular-nums">
                    <Value value={c} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// One table at a time; the switch on top picks the plan family, like the toggle above the cards.
export function PlanComparisonTables() {
  const [family, setFamily] = useState<'standard' | 'byok'>('standard')
  const offer = useLaunchOffer()
  const tables = { standard: OLIVE_PLANS, byok: byokTable(offer) }
  return (
    <div>
      <div role="tablist" aria-label="Plan family" className="inline-flex rounded-xl border border-white/10 p-1">
        {(['standard', 'byok'] as const).map(f => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={family === f}
            onClick={() => {
              setFamily(f)
              trackPricingCompareTableSwitched({ table: f })
            }}
            className={`rounded-lg px-4 py-2 text-sm transition-colors ${
              family === f ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white/80'
            }`}
          >
            {tables[f].title}
          </button>
        ))}
      </div>
      <div className="mt-8">
        <PlanTable table={tables[family]} />
      </div>
    </div>
  )
}
